/**
 * Control Centre Orders page, built around what is wrong, not what is done.
 *
 *   header          period, refresh, customise layout
 *   needs a decision / data-quality banners
 *   three cards     Deliveries · Collections · ETAs, each leading with how many are NOT done
 *   sections        in the user's saved order, each collapsible:
 *                   needs action (the open orders, most urgent first, with detail)
 *                   all orders   (status breakdown, then the full table)
 *                   trend
 *
 * Every figure comes from the on-demand orders_daily projection. Where the page
 * decides something is an exception it says why in words (utils/ordersExceptions).
 */

import { Fragment, useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { formatYmd } from '../../utils/ukTime';
import { ChevronDown, ChevronRight, Download, RefreshCw } from 'lucide-react';
import BarChart from '../BarChart';
import NeedsADecisionStrip from './NeedsADecisionStrip';
import OrdersBreakdownStrip, { type BreakdownLane } from './OrdersBreakdownStrip';
import OrdersDailyDrilldownTable from './OrdersDailyDrilldownTable';
import OrdersExceptionWorkspace from './OrdersExceptionWorkspace';
import OrdersLaneCards from './OrdersLaneCards';
import OrdersLayoutPanel from './OrdersLayoutPanel';
import { useOrdersDailyProjection } from '../../hooks/useOrdersDailyProjection';
import { useOrdersDailySeries } from '../../hooks/useOrdersDailySeries';
import { useOrdersPeriods } from '../../hooks/useOrdersPeriods';
import { useOrdersUiPreferences } from '../../hooks/useOrdersUiPreferences';
import type { OrdersDailyRow } from '../../types/ordersDailyTruth';
import {
  ORDERS_UI_SECTION_LABELS,
  type OrdersUiSection,
} from '../../types/ordersUiPreferences';
import {
  addUtcDays,
  formatConsignments,
  formatDataCurrentAs,
  formatLifts,
  isOrdersDailyLondonTodayRange,
  londonDateString,
  matchesOrdersDailyKpi,
  ordersDailySeriesCoverageNote,
  ordersDailyRangeIncludesLondonToday,
  type OrdersDailyKpiFilter,
} from '../../utils/ordersDailyPresentation';
import {
  buildOrdersExceptions,
  countExpiringEta,
  countNeedingAction,
  countOutsideEta,
  londonWallNow,
  type OrdersExceptionLane,
} from '../../utils/ordersExceptions';
import {
  buildTrendBarGroupsFromSeries,
  CHART_LOOKBACK_OPTIONS,
  CHART_METRIC_OPTIONS,
  downloadTrendChartCsv,
  TREND_CSV_LOOKBACK_DAYS,
  trendSeriesLegendItems,
  type ChartLookbackDays,
  type ChartMetricKind,
} from '../../utils/ordersDailyRates';
import {
  buildTrendBarGroupsFromPeriods,
  periodTrendCoverageNote,
  periodTrendRange,
  TREND_GRAIN_OPTIONS,
  type TrendGrain,
} from '../../utils/ordersPeriodTrend';


interface OrdersDailyKpiSectionProps {
  startDate: string;
  endDate: string;
  highlightKPI?: string | null;
  onHighlightConsumed?: () => void;
  /** Period chips on the same row as Refresh figures (right-aligned). */
  periodBar?: ReactNode;
  /** Admin Demo toggle — same UI, offline fixtures. */
  demo?: boolean;
}

function sameFilter(a: OrdersDailyKpiFilter, b: OrdersDailyKpiFilter): boolean {
  if (a.kind !== b.kind) return false;
  if (a.kind === 'bucket' && b.kind === 'bucket') return a.bucket === b.bucket;
  return true;
}

/** Which breakdown lane a table filter belongs to, so a link lands on the right one. */
function laneForFilter(filter: OrdersDailyKpiFilter): BreakdownLane {
  switch (filter.kind) {
    case 'collections':
    case 'collected':
    case 'collection_stage':
    case 'collection_without_poc':
    case 'collection_not_received':
    case 'collection_condition':
    case 'collection_source':
      return 'collections';
    case 'eta':
    case 'eta_late':
      return 'etas';
    case 'all':
    case 'open':
    case 'completion':
      return 'all';
    case 'bucket':
      return filter.direction === 'collection' ? 'collections' : 'deliveries';
    default:
      return 'deliveries';
  }
}

/** Filters the breakdown tiles do not cover. */
const OTHER_VIEWS: Array<{ id: string; label: string; filter: OrdersDailyKpiFilter }> = [
  { id: 'all', label: 'All in period', filter: { kind: 'all' } },
  { id: 'eta_late', label: 'Late vs ETA', filter: { kind: 'eta_late' } },
  { id: 'tap_issues', label: 'Arrival / POD tap issues', filter: { kind: 'tap_issues' } },
  { id: 'cancelled', label: 'Cancelled', filter: { kind: 'cancelled' } },
];

const ALL_LEGS_FILTER: OrdersDailyKpiFilter = { kind: 'all' };

/** Why a stored snapshot is out of date (zeecore `SnapshotStaleReason`), in words. */
const STALE_REASON_TEXT: Record<string, string> = {
  too_old: 'they have not been refreshed recently',
  config: 'the KPI settings have changed since they were computed',
  rules_version: 'the calculation rules have changed since they were computed',
  owned_depots: 'the owned depots have changed since they were computed',
  late_updates: 'later updates to some days are still being added',
};

/** "12 Aug" or "1 Aug to 3 Aug (3 days)" — the days a stored range has no figures for. */
function unavailableDaysText(days: ReadonlyArray<{ date: string }>): string {
  const label = (ymd: string) => formatYmd(ymd, { day: 'numeric', month: 'short' });
  if (days.length === 1) return label(days[0].date);
  return `${label(days[0].date)} to ${label(days[days.length - 1].date)} (${days.length} days)`;
}

/**
 * Says the stale legs were not looked for, never that there are none. They cost
 * a much wider scan, so they load only when asked for.
 */
function StaleNotLoaded({
  id,
  afterDays,
  loading,
  onLoad,
}: {
  id: string;
  afterDays: number;
  loading: boolean;
  onLoad: () => void;
}) {
  return (
    <div id={id} className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
      Older open orders ({afterDays} or more days past their date) are not loaded.{' '}
      <button
        type="button"
        onClick={onLoad}
        disabled={loading}
        className="font-medium text-ring hover:text-ring disabled:text-muted-foreground disabled:cursor-wait"
      >
        {loading ? 'Loading stale orders…' : 'Load stale orders'}
      </button>
    </div>
  );
}

/** One collapsible section of the page. */
function PagePanel({
  id,
  title,
  summary,
  open,
  onToggle,
  panelRef,
  children,
}: {
  id: string;
  title: string;
  summary?: string;
  open: boolean;
  onToggle: () => void;
  panelRef?: React.Ref<HTMLElement>;
  children: ReactNode;
}) {
  return (
    <section ref={panelRef} id={id}>
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide-3 text-muted-foreground hover:text-foreground mb-3"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {title}
        {summary && <span className="normal-case tracking-normal font-normal text-muted-foreground">{summary}</span>}
      </button>
      {open && <div id={`${id}-panel`}>{children}</div>}
    </section>
  );
}

/** A collapsible, table-backed list of legs set apart from today's figures. Renders nothing when empty. */
function BacklogSection({
  id,
  title,
  rows,
  description,
  exportName,
  defaultOpen = false,
}: {
  id: string;
  title: string;
  rows: OrdersDailyRow[];
  description: string;
  exportName: string;
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (rows.length === 0) return null;
  return (
    <section id={id}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={`${id}-panel`}
        className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide-3 text-muted-foreground hover:text-foreground mb-3"
      >
        {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        {title}
        <span className="normal-case tracking-normal font-normal text-muted-foreground">
          ({rows.length.toLocaleString()} {rows.length === 1 ? 'leg' : 'legs'}{open ? '' : ' — open to review'})
        </span>
      </button>
      {open && (
        <div id={`${id}-panel`} className="space-y-3">
          <p className="text-xs text-muted-foreground max-w-3xl">{description}</p>
          <OrdersDailyDrilldownTable
            rows={rows}
            loading={false}
            emptyMessage={`No ${title.toLowerCase()} legs.`}
            filter={ALL_LEGS_FILTER}
            exportName={exportName}
          />
        </div>
      )}
    </section>
  );
}

export default function OrdersDailyKpiSection({
  startDate,
  endDate,
  highlightKPI,
  onHighlightConsumed,
  periodBar,
  demo = false,
}: OrdersDailyKpiSectionProps) {
  const viewingLondonToday = isOrdersDailyLondonTodayRange(startDate, endDate);
  const rangeIncludesLondonToday = ordersDailyRangeIncludesLondonToday(startDate, endDate);
  // Stale legs cost a much wider scan, so they load only when asked for, and
  // the request does not carry over to another period.
  const periodKey = `${startDate}|${endDate}`;
  const [staleRequestedFor, setStaleRequestedFor] = useState<string | null>(null);
  const staleRequested = staleRequestedFor === periodKey;

  const {
    summary,
    rows,
    overdue: overdueAll,
    stale: staleAll,
    cancelled,
    staleLoading,
    rowsLoading,
    loading,
    error,
    lastLoadedAt,
    projection,
    preparing,
    refresh,
    range,
  } = useOrdersDailyProjection(startDate, endDate, {
    demo,
    includeBacklog: rangeIncludesLondonToday,
    includeStale: staleRequested,
  });

  // Trend chart lookback is independent of the period chips so gauges stay on
  // “today” (or the selected day) while the bars show the last N London days.
  const [chartLookbackDays, setChartLookbackDays] = useState<ChartLookbackDays>(7);
  const [chartMetric, setChartMetric] = useState<ChartMetricKind>('completion_rate');
  // Days come from the stored day summaries; weeks and longer from the stored
  // period records (built from order-leg facts) — neither is computed on a request.
  const [trendGrain, setTrendGrain] = useState<TrendGrain>('day');
  const chartEndDate = rangeIncludesLondonToday ? londonDateString() : endDate;
  const chartStartDate = addUtcDays(chartEndDate, -(chartLookbackDays - 1));
  const exportStartDate = addUtcDays(chartEndDate, -(TREND_CSV_LOOKBACK_DAYS - 1));

  // Chart already loaded ≥30d — reuse for CSV without a third fetch.
  const chartCoversExport = chartLookbackDays >= TREND_CSV_LOOKBACK_DAYS;

  // Always the stored per-day summaries (the server never computes these on
  // a request) — even when the selected period's rows happen to cover the
  // window, so a bar means the same thing whichever period chip is selected.
  const {
    days: chartDays,
    staleDays: chartStaleDays,
    unavailableDays: chartUnavailableDays,
    loading: chartLoading,
    error: chartError,
    preparing: chartPreparing,
    lastLoadedAt: chartFetchLoadedAt,
    refresh: refreshChart,
  } = useOrdersDailySeries(chartStartDate, chartEndDate, { demo });
  // Unsettled empty (no successful load yet) stays on loading so we never
  // flash the empty copy mid-flight; a settled empty lookback shows "No consignments…".
  const chartSettled = chartFetchLoadedAt != null;

  const periodGrain = trendGrain === 'day' ? 'week' : trendGrain;
  const periodRange = useMemo(() => periodTrendRange(periodGrain, chartEndDate), [periodGrain, chartEndDate]);
  const {
    periods: trendPeriods,
    unavailable: trendPeriodsUnavailable,
    stale: trendPeriodsStale,
    loading: periodsLoading,
    error: periodsError,
    lastLoadedAt: periodsLoadedAt,
    refresh: refreshPeriods,
    supported: periodsSupported,
  } = useOrdersPeriods(periodGrain, periodRange.from, periodRange.to, { demo, enabled: trendGrain !== 'day' });

  // CSV is always 30d; load that window only after the user asks (or when the
  // chart already covers it).
  const [exportFetchArmed, setExportFetchArmed] = useState(false);
  const [pendingCsvDownload, setPendingCsvDownload] = useState(false);
  const needExportFetch = !chartCoversExport;

  const {
    days: exportFetchDays,
    unavailableDays: exportFetchUnavailableDays,
    loading: exportFetchLoading,
    error: exportFetchError,
    refresh: refreshExport,
  } = useOrdersDailySeries(exportStartDate, chartEndDate, {
    demo,
    enabled: needExportFetch && exportFetchArmed,
  });

  const exportDays = chartCoversExport ? chartDays : exportFetchDays;
  const exportUnavailableDays = chartCoversExport ? chartUnavailableDays : exportFetchUnavailableDays;

  const [exportCsvMessage, setExportCsvMessage] = useState<string | null>(null);

  // After the primary poll (or first load), refresh the lookback fetch so the
  // trend stays current without its own 5-minute interval.
  const prevGaugeLoadedRef = useRef<string | null>(null);
  useEffect(() => {
    prevGaugeLoadedRef.current = null;
  }, [chartStartDate, chartEndDate, demo]);
  useEffect(() => {
    if (!lastLoadedAt) return;
    if (prevGaugeLoadedRef.current === null) {
      prevGaugeLoadedRef.current = lastLoadedAt;
      return;
    }
    if (prevGaugeLoadedRef.current === lastLoadedAt) return;
    prevGaugeLoadedRef.current = lastLoadedAt;
    void refreshChart({ background: true });
    if (trendGrain !== 'day') void refreshPeriods({ background: true });
    if (needExportFetch && exportFetchArmed) void refreshExport({ background: true });
  }, [
    lastLoadedAt,
    refreshChart,
    trendGrain,
    refreshPeriods,
    needExportFetch,
    exportFetchArmed,
    refreshExport,
  ]);

  const chartAsOf = useMemo(
    () => new Date(`${chartEndDate}T12:00:00.000Z`),
    [chartEndDate],
  );
  const chartBarGroups = useMemo(
    () =>
      buildTrendBarGroupsFromSeries(chartDays, chartMetric, chartLookbackDays, {
        asOf: chartAsOf,
        todayDate: londonDateString(),
        thinLabels: true,
        endDate: chartEndDate,
      }),
    [chartDays, chartLookbackDays, chartAsOf, chartMetric, chartEndDate],
  );
  const periodBarGroups = useMemo(
    () => buildTrendBarGroupsFromPeriods(periodGrain, trendPeriods, chartMetric, londonDateString()),
    [periodGrain, trendPeriods, chartMetric],
  );
  const byPeriod = trendGrain !== 'day';
  const trendBarGroups = byPeriod ? periodBarGroups : chartBarGroups;
  const trendError = byPeriod ? periodsError : chartError;
  const chartShowLoading =
    trendBarGroups.length === 0
    && !trendError
    && (byPeriod
      ? periodsLoading || periodsLoadedAt == null
      : chartLoading || chartPreparing || !chartSettled);

  const exportBarGroups = useMemo(
    () =>
      buildTrendBarGroupsFromSeries(exportDays, chartMetric, TREND_CSV_LOOKBACK_DAYS, {
        asOf: chartAsOf,
        todayDate: londonDateString(),
        endDate: chartEndDate,
      }),
    [exportDays, chartAsOf, chartMetric, chartEndDate],
  );

  const runTrendCsvDownload = useCallback(() => {
    downloadTrendChartCsv(exportBarGroups, {
      metric: chartMetric,
      lookbackDays: TREND_CSV_LOOKBACK_DAYS,
      endDate: chartEndDate,
    });
  }, [exportBarGroups, chartMetric, chartEndDate]);

  /** Download what is stored; say which days are missing rather than export them as blanks silently. */
  const finishCsvExport = useCallback(() => {
    const preparing = exportUnavailableDays.some((d) => d.reason === 'not_computed');
    if (exportBarGroups.length === 0) {
      setExportCsvMessage(preparing
        ? 'The 30-day figures are still being prepared — try again in a few minutes.'
        : 'No consignments in the 30-day window to export.');
      return;
    }
    runTrendCsvDownload();
    const missing = exportUnavailableDays.length;
    if (missing > 0) {
      setExportCsvMessage(`CSV leaves out ${missing} day${missing === 1 ? '' : 's'} with no stored figures yet.`);
    }
  }, [exportBarGroups.length, exportUnavailableDays, runTrendCsvDownload]);

  const handleExportCsv = useCallback(() => {
    setExportCsvMessage(null);
    if (!needExportFetch) {
      finishCsvExport();
      return;
    }
    if (exportFetchArmed && !exportFetchLoading && exportBarGroups.length > 0) {
      finishCsvExport();
      return;
    }
    setExportFetchArmed(true);
    setPendingCsvDownload(true);
  }, [
    needExportFetch,
    exportBarGroups.length,
    exportFetchArmed,
    exportFetchLoading,
    finishCsvExport,
  ]);

  useEffect(() => {
    if (!pendingCsvDownload) return;
    if (exportFetchLoading) return;
    if (exportFetchError) {
      setExportCsvMessage(exportFetchError);
      setPendingCsvDownload(false);
      return;
    }
    finishCsvExport();
    setPendingCsvDownload(false);
  }, [
    pendingCsvDownload,
    exportFetchLoading,
    exportFetchError,
    finishCsvExport,
  ]);

  const chartLegendItems = useMemo(
    () => trendSeriesLegendItems(trendBarGroups),
    [trendBarGroups],
  );
  const chartHasEtaSeries = chartLegendItems.some((s) => s.name === 'ETA%');
  const chartCoverageNote = byPeriod
    ? periodTrendCoverageNote(periodGrain, trendPeriods, trendPeriodsUnavailable, trendPeriodsStale)
    : ordersDailySeriesCoverageNote(chartUnavailableDays, chartStaleDays);
  const chartAllPreparing = trendBarGroups.length === 0 && (byPeriod
    ? trendPeriodsUnavailable.some((p) => p.reason === 'not_computed')
    : chartUnavailableDays.some((d) => d.reason === 'not_computed'));

  const chartMetricLabel =
    CHART_METRIC_OPTIONS.find((o) => o.id === chartMetric)?.label ?? 'Completion rates';

  const refreshAll = useCallback(() => {
    void refresh();
    void refreshChart();
    if (trendGrain !== 'day') void refreshPeriods();
    if (needExportFetch && exportFetchArmed) void refreshExport();
  }, [refresh, refreshChart, trendGrain, refreshPeriods, needExportFetch, exportFetchArmed, refreshExport]);

  const [tableFilter, setTableFilter] = useState<OrdersDailyKpiFilter>({ kind: 'all' });
  const [tableSearch, setTableSearch] = useState<string | undefined>(undefined);
  // Which lane the breakdown above the All orders table is showing.
  const [breakdownLane, setBreakdownLane] = useState<BreakdownLane>('all');
  // Following a link (a lane's "Open all", a consignment) opens All orders for
  // this visit only. Whether the section stays open next time is the user's
  // own choice, saved on their profile.
  const [allOrdersPeek, setAllOrdersPeek] = useState(false);
  const drillRef = useRef<HTMLElement>(null);
  const staleAfterDays = summary?.stale?.after_days ?? 7;

  const applyFilter = useCallback((filter: OrdersDailyKpiFilter) => {
    setTableFilter(filter);
    setBreakdownLane(laneForFilter(filter));
    setAllOrdersPeek(true);
    // Let the panel mount before scrolling to it.
    requestAnimationFrame(() => {
      drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  const openConsignment = useCallback((trackingId: string) => {
    setTableFilter({ kind: 'all' });
    setTableSearch(trackingId);
    setBreakdownLane('all');
    setAllOrdersPeek(true);
    requestAnimationFrame(() => {
      drillRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  }, []);

  // At-risk is today-only; clear a stale chip when the period no longer includes London today.
  useEffect(() => {
    if (!rangeIncludesLondonToday && tableFilter.kind === 'at_risk_1700') {
      setTableFilter({ kind: 'all' });
    }
  }, [rangeIncludesLondonToday, tableFilter.kind]);

  useEffect(() => {
    if (!highlightKPI) return;
    const map: Record<string, OrdersDailyKpiFilter> = {
      collections: { kind: 'collections' },
      deliveries: { kind: 'deliveries' },
      active: { kind: 'deliveries' },
      completed: { kind: 'deliveries' },
    };
    const match = map[highlightKPI];
    if (match) applyFilter(match);
    onHighlightConsumed?.();
  }, [highlightKPI, onHighlightConsumed, applyFilter]);

  const { prefs, status: prefsStatus, saveError: prefsSaveError, update: updatePrefs } = useOrdersUiPreferences({ demo });
  const etaWarnMinutes = prefs.eta_warning_minutes;
  const [lane, setLane] = useState<OrdersExceptionLane>('deliveries');

  // The ETA clock needs "now". Tick each minute so an ETA that runs out while
  // the page is open flips without waiting for the next data refresh.
  const [nowWall, setNowWall] = useState(() => londonWallNow());
  useEffect(() => {
    const id = window.setInterval(() => setNowWall(londonWallNow()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  useEffect(() => {
    if (lastLoadedAt) setNowWall(londonWallNow());
  }, [lastLoadedAt]);

  // A hired haulier's leg it has not reported is set apart from the org's own
  // overdue and stale work (`carrier_not_reported`): still open, own section.
  const overdue = useMemo(() => overdueAll.filter((r) => !r.carrier_not_reported), [overdueAll]);
  const stale = useMemo(() => staleAll.filter((r) => !r.carrier_not_reported), [staleAll]);
  const carrierNotReported = useMemo(
    () => [...overdueAll, ...staleAll].filter((r) => r.carrier_not_reported),
    [overdueAll, staleAll],
  );

  const exceptions = useMemo(
    () => buildOrdersExceptions({
      rows,
      overdue,
      nowWall,
      etaWarnMinutes,
      includeEta: rangeIncludesLondonToday,
    }),
    [rows, overdue, nowWall, etaWarnMinutes, rangeIncludesLondonToday],
  );
  const overdueDeliveries = overdue.filter((r) => r.direction === 'delivery').length;
  const overdueCollections = overdue.filter((r) => r.direction === 'collection').length;

  const sectionOpen = (section: OrdersUiSection): boolean =>
    section === 'all_orders'
      ? prefs.sections_open.all_orders || allOrdersPeek
      : prefs.sections_open[section];
  const toggleSection = (section: OrdersUiSection) => {
    const next = !sectionOpen(section);
    updatePrefs({ sections_open: { [section]: next } });
    if (section === 'all_orders') setAllOrdersPeek(false);
  };
  const selectLane = (next: OrdersExceptionLane) => {
    setLane(next);
    if (!prefs.sections_open.workspace) updatePrefs({ sections_open: { workspace: true } });
    // Bring the three cards to the top, just under the sticky section tabs, so
    // the cards and the Needs action list under them are in view together.
    // scrollIntoView, not window.scrollBy: the app scrolls inside its own
    // container (App.tsx), not the window.
    window.requestAnimationFrame(() => {
      const cards = document.getElementById('orders-lane-cards');
      if (!cards) return;
      const nav = document.querySelector<HTMLElement>('[data-sticky-section-nav]');
      cards.style.scrollMarginTop = `${(nav?.offsetHeight ?? 0) + 12}px`;
      cards.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
    });
  };
  const openLaneInTable = (next: OrdersExceptionLane) => {
    applyFilter(next === 'collections' ? { kind: 'collections' } : next === 'etas' ? { kind: 'eta' } : { kind: 'deliveries' });
  };
  const loadStale = () => setStaleRequestedFor(periodKey);

  const dataCurrentAs = formatDataCurrentAs(lastLoadedAt);
  const noProjection = !loading && !error && !preparing && (summary?.row_count ?? 0) === 0;
  // A direction whose operational-date rule is not yet confirmed is badged
  // rather than presented as settled (both are confirmed for Palletways).
  const collectionsProvisional = summary?.data_quality?.collection_date_semantics === 'provisional';
  const deliveriesProvisional = summary?.data_quality?.delivery_date_semantics === 'provisional';

  // The orders table lists the period's cancelled legs too, labelled Cancelled;
  // the cards and counts above come from the server's figures, which leave them out.
  const tableRows = useMemo(() => (cancelled.length > 0 ? [...rows, ...cancelled] : rows), [rows, cancelled]);
  const chipCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    for (const chip of OTHER_VIEWS) {
      counts[chip.id] = tableRows.filter((row) => matchesOrdersDailyKpi(row, chip.filter)).length;
    }
    return counts;
  }, [tableRows]);

  const laneItems = exceptions[lane];
  const staleFooter = (id: string) => (summary?.stale?.loaded === false ? (
    <StaleNotLoaded id={id} afterDays={staleAfterDays} loading={staleLoading} onLoad={loadStale} />
  ) : stale.length > 0 ? (
    <p className="mt-4 border-t border-border pt-3 text-xs text-muted-foreground">
      {stale.length.toLocaleString()} stale {stale.length === 1 ? 'order is' : 'orders are'} loaded.{' '}
      <button
        type="button"
        className="font-medium text-ring hover:text-ring"
        onClick={() => {
          setAllOrdersPeek(true);
          requestAnimationFrame(() => {
            document.getElementById('orders-daily-stale')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          });
        }}
      >
        Show them in All orders
      </button>
    </p>
  ) : null);

  const renderSection = (section: OrdersUiSection): ReactNode => {
    const open = sectionOpen(section);
    if (section === 'workspace') {
      const counts = `Deliveries ${countNeedingAction(exceptions.deliveries)} · Collections ${countNeedingAction(exceptions.collections)} · ETAs ${countOutsideEta(exceptions.etas)}`;
      return (
        <PagePanel
          id="orders-section-workspace"
          title={ORDERS_UI_SECTION_LABELS.workspace}
          summary={open ? undefined : counts}
          open={open}
          onToggle={() => toggleSection('workspace')}
        >
          <OrdersExceptionWorkspace
            lane={lane}
            items={laneItems}
            loading={rowsLoading}
            etaWarnMinutes={etaWarnMinutes}
            onEtaWarnChange={(minutes) => updatePrefs({ eta_warning_minutes: minutes })}
            onOpenInTable={openConsignment}
            onOpenLane={openLaneInTable}
            footer={lane === 'etas' ? null : staleFooter('orders-workspace-stale')}
          />
        </PagePanel>
      );
    }
    if (section === 'all_orders') {
      return (
        <PagePanel
          id="orders-daily-drilldown"
          panelRef={drillRef}
          title={ORDERS_UI_SECTION_LABELS.all_orders}
          summary={summary ? `${summary.row_count.toLocaleString()} legs · search, check a consignment or export` : undefined}
          open={open}
          onToggle={() => toggleSection('all_orders')}
        >
          <div className="space-y-4">
            <OrdersBreakdownStrip
              summary={summary}
              lane={breakdownLane}
              onLane={setBreakdownLane}
              tableFilter={tableFilter}
              onFilter={(filter) => { setTableFilter(filter); setTableSearch(undefined); }}
              startDate={startDate}
              endDate={endDate}
              viewingLondonToday={viewingLondonToday}
              rangeIncludesLondonToday={rangeIncludesLondonToday}
            />
            <div className="flex flex-wrap gap-2">
              {OTHER_VIEWS.map((chip) => {
                const selected = sameFilter(tableFilter, chip.filter);
                const count = rows.length > 0 || !rowsLoading
                  ? chipCounts[chip.id]
                  : chip.id === 'all' ? summary?.row_count : undefined;
                return (
                  <button
                    key={chip.id}
                    type="button"
                    onClick={() => { setTableFilter(chip.filter); setTableSearch(undefined); setBreakdownLane(laneForFilter(chip.filter)); }}
                    className={`rounded-full border px-3.5 py-1.5 text-xs ${
                      selected
                        ? 'bg-chart-5 border-chart-5 text-white font-semibold'
                        : 'bg-card/80 border-border text-foreground hover:border-muted-foreground'
                    }`}
                  >
                    {chip.label}
                    {count != null ? ` (${count})` : ''}
                  </button>
                );
              })}
            </div>
            {(projection?.cancelled_unrecorded_days?.length ?? 0) > 0 && (
              <p className="text-xs text-muted-foreground" data-testid="orders-cancelled-unrecorded">
                {`Cancelled orders are not listed for ${unavailableDaysText(projection!.cancelled_unrecorded_days!.map((date) => ({ date })))}: those days were read before cancellations were recorded.`}
              </p>
            )}
            <OrdersDailyDrilldownTable
              rows={tableRows}
              loading={rowsLoading && rows.length === 0}
              emptyMessage={
                noProjection
                  ? (demo
                    ? 'No demo orders in this period.'
                    : 'No orders for this period. Check Palletways ingest and your home depot\'s Owned flag.')
                  : 'No legs match this filter.'
              }
              filter={tableFilter}
              searchTerm={tableSearch}
              exportName={`orders_${tableFilter.kind}_${startDate}.csv`}
            />
            <BacklogSection
              id="orders-daily-overdue"
              title="Overdue"
              rows={overdue}
              defaultOpen
              exportName={`orders_overdue_${startDate}.csv`}
              description={`Open deliveries and collections past their date by less than ${staleAfterDays} days. Not counted in the cards above, which are today's work only.`}
            />
            {summary?.stale?.loaded === false ? (
              <StaleNotLoaded
                id="orders-daily-stale"
                afterDays={staleAfterDays}
                loading={staleLoading}
                onLoad={loadStale}
              />
            ) : (
              <BacklogSection
                id="orders-daily-stale"
                title="Stale"
                rows={stale}
                exportName={`orders_stale_${startDate}.csv`}
                description={`Open deliveries and collections ${staleAfterDays} or more days past their date. Not counted above. They are not confirmed complete either, since Palletways has recorded nothing to say so, so check each one and close it at source.`}
              />
            )}
            {carrierNotReported.length > 0 ? (
              <BacklogSection
                id="orders-daily-carrier-not-reported"
                title="Not reported by carrier"
                rows={carrierNotReported}
                exportName={`orders_carrier_not_reported_${startDate}.csv`}
                description="Open deliveries and collections handed to hired hauliers, past their date, that the haulier has not marked done in the source system. Kept apart from your own overdue work. Not counted as done: check with the haulier and close each one at source."
              />
            ) : null}
          </div>
        </PagePanel>
      );
    }
    return (
      <PagePanel
        id="orders-section-trend"
        title={ORDERS_UI_SECTION_LABELS.trend}
        summary={open ? undefined : chartMetricLabel}
        open={open}
        onToggle={() => toggleSection('trend')}
      >
        <div>
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div className="flex flex-wrap items-center gap-2 min-w-0">
            <div
              className="inline-flex flex-wrap gap-1"
              role="group"
              aria-label="Chart metric"
            >
              {CHART_METRIC_OPTIONS.map((opt) => {
                const selected = chartMetric === opt.id;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setChartMetric(opt.id)}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors ${
                      selected
                        ? 'bg-chart-5/20 text-chart-5 ring-1 ring-chart-5/40'
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              onClick={handleExportCsv}
              disabled={
                pendingCsvDownload
                || exportFetchLoading
                || (!needExportFetch && exportBarGroups.length === 0)
              }
              className="inline-flex items-center gap-1.5 rounded-md border border-border bg-card/80 px-2.5 py-1 text-xs font-medium text-foreground hover:bg-secondary hover:text-white disabled:opacity-40 disabled:pointer-events-none transition-colors"
              title="Download last 30 days of trend figures (independent of the chart lookback)"
            >
              <Download className="w-3.5 h-3.5" aria-hidden />
              {pendingCsvDownload || exportFetchLoading ? 'Preparing 30d…' : 'Export 30d CSV'}
            </button>
            {exportCsvMessage ? (
              <p className="text-xs text-warning/90 max-w-56 leading-snug" role="status">
                {exportCsvMessage}
              </p>
            ) : null}
            <div className="flex items-center gap-1" role="group" aria-label="Trend grain">
              {TREND_GRAIN_OPTIONS.map((opt) => {
                const selected = trendGrain === opt.id;
                const unavailable = opt.id !== 'day' && !periodsSupported;
                return (
                  <button
                    key={opt.id}
                    type="button"
                    onClick={() => setTrendGrain(opt.id)}
                    disabled={unavailable}
                    title={unavailable ? 'Not available in demo mode' : undefined}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium transition-colors disabled:opacity-40 disabled:pointer-events-none ${
                      selected
                        ? 'bg-chart-5/20 text-chart-5 ring-1 ring-chart-5/40'
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                    }`}
                  >
                    {opt.label}
                  </button>
                );
              })}
            </div>
            {!byPeriod && (
            <div className="flex items-center gap-1" role="group" aria-label="Chart lookback">
              {CHART_LOOKBACK_OPTIONS.map((days) => {
                const selected = chartLookbackDays === days;
                return (
                  <button
                    key={days}
                    type="button"
                    onClick={() => setChartLookbackDays(days)}
                    className={`rounded-md px-2.5 py-1 text-xs font-medium tabular-nums transition-colors ${
                      selected
                        ? 'bg-chart-5/20 text-chart-5 ring-1 ring-chart-5/40'
                        : 'text-muted-foreground hover:text-foreground hover:bg-secondary/60'
                    }`}
                  >
                    {days}d
                  </button>
                );
              })}
            </div>
            )}
          </div>
        </div>
        {chartShowLoading ? (
          <div className="h-72 flex items-center justify-center text-sm text-muted-foreground rounded-lg border border-border/60 bg-background/40">
            Loading {chartMetricLabel.toLowerCase()}…
          </div>
        ) : trendError && trendBarGroups.length === 0 ? (
          <div className="h-72 flex items-center justify-center text-sm text-warning rounded-lg border border-border/60 bg-background/40">
            {trendError}
          </div>
        ) : chartAllPreparing ? (
          <div className="h-72 flex items-center justify-center text-sm text-muted-foreground rounded-lg border border-border/60 bg-background/40">
            Trend figures for these {byPeriod ? 'periods' : 'days'} are still being prepared — they appear here within a few minutes.
          </div>
        ) : trendBarGroups.length === 0 ? (
          <div className="h-72 flex items-center justify-center text-sm text-muted-foreground rounded-lg border border-border/60 bg-background/40">
            {chartCoverageNote ?? (byPeriod ? 'No consignments in these periods.' : 'No consignments in this lookback window.')}
          </div>
        ) : (
          <div className="rounded-lg border border-border/60 bg-background/40 overflow-hidden">
            <div className="px-2 pt-2 sm:px-3 sm:pt-3">
              {/* Darcica, Nov 2026: too much dead space. The plot fills a taller box
                  (compact alone draws 152 px in it), spans the full width at any browser zoom,
                  and a day's bars use most of its slot, up to 48 px each. */}
              <BarChart
                data={trendBarGroups}
                height="h-72"
                plotHeight={264}
                groupWidthFraction={0.85}
                maxGroupWidth={Infinity}
                maxBarWidth={48}
                fillGroup
                fillWidth
                hoverValueFontSize={14}
                axisFontSize={13}
                compact
                hideLegend
                accent="purple"
                yAxisSuffix={chartMetric === 'completion_rate' ? '%' : undefined}
              />
            </div>
            <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-3 py-2.5 border-t border-border/60 bg-background/40">
              <div
                className="flex flex-wrap items-center gap-x-3 gap-y-1.5"
                role="list"
                aria-label="Chart series"
              >
                {chartLegendItems.map((item) => (
                  <div key={item.name} className="flex items-center gap-1.5" role="listitem">
                    <span
                      className="inline-block w-2.5 h-2.5 rounded-sm shrink-0"
                      style={{ backgroundColor: item.color }}
                      aria-hidden
                    />
                    <span className="text-xs text-foreground">{item.name}</span>
                  </div>
                ))}
              </div>
              {byPeriod && (
                <p className="text-xs text-muted-foreground min-w-0">
                  Due in period — by due / collection date, same rules as the daily bars
                </p>
              )}
              {chartMetric === 'completion_rate' && (
                <p className="text-xs text-muted-foreground min-w-0 sm:ml-auto">
                  {chartHasEtaSeries
                    ? 'ETA% — driver arrival (else POD) vs Palletways delivery window (delivered with a PDT estimate only)'
                    : 'No PDT estimates in this lookback — ETA% omitted'}
                </p>
              )}
              {chartCoverageNote && (
                <p className="basis-full text-xs text-warning/90" role="status">
                  {chartCoverageNote}
                </p>
              )}
            </div>
          </div>
        )}
        </div>
      </PagePanel>
    );
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <div className="flex items-center justify-between gap-4">
          {periodBar}
          <div className="ml-auto flex items-center gap-2 shrink-0">
          <OrdersLayoutPanel
            order={prefs.section_order}
            status={prefsStatus}
            saveError={prefsSaveError}
            onChange={(order) => updatePrefs({ section_order: order })}
            cardHeadline={prefs.card_headline}
            onCardHeadlineChange={(headline) => updatePrefs({ card_headline: headline })}
          />
          <button
            type="button"
            onClick={() => refreshAll()}
            disabled={loading || rowsLoading}
            className="inline-flex items-center gap-2 shrink-0 rounded-lg bg-chart-5 hover:bg-chart-5 disabled:opacity-50 px-3 py-1.5 text-sm font-medium text-white"
          >
            <RefreshCw className={`w-4 h-4 ${loading || rowsLoading ? 'animate-spin' : ''}`} />
            {loading || rowsLoading ? 'Refreshing…' : 'Refresh figures'}
          </button>
          </div>
        </div>
        <div className="text-sm text-foreground min-w-0">
          {dataCurrentAs && !error && (
            <span>
              Data current as of {dataCurrentAs}
            </span>
          )}
          {preparing && (
            <span className="block text-ring mt-1">
              Preparing the figures for this period — usually a minute or two. This page will update on its own.
            </span>
          )}
          {projection?.stale_reason && !preparing && (
            <span className="block text-warning mt-1">
              {`These figures are out of date: ${STALE_REASON_TEXT[projection.stale_reason] ?? 'they are overdue a refresh'}. `
                + 'A refresh has been requested and this page will update when it lands.'}
            </span>
          )}
          {!preparing && (projection?.unavailable_days?.length ?? 0) > 0 && (
            <span className="block text-warning mt-1">
              {`No stored figures for ${unavailableDaysText(projection!.unavailable_days!)}: `
                + 'those days are not included in these figures.'}
            </span>
          )}
          {noProjection && (
            <span>
              {demo
                ? 'No demo orders in this period.'
                : 'No orders in this period. Check Palletways ingest — and mark your home depot\'s Owned flag on Assets.'}
            </span>
          )}
          {range.clamped && (
            <span className="block text-xs text-warning mt-1">
              Showing the latest 93 days of this range.
            </span>
          )}
          {error && (
            <span className="block text-warning mt-1">{error}</span>
          )}
        </div>
      </div>

      <NeedsADecisionStrip rows={rows} loading={rowsLoading && rows.length === 0} onOpen={openConsignment} />

      {(summary?.unmapped_status.consignments ?? 0) > 0 && (
        <button
          type="button"
          id="kpi-unmapped"
          onClick={() => applyFilter({ kind: 'bucket', bucket: 'unmapped', direction: 'delivery' })}
          className="w-full text-left rounded-lg border border-warning/40 bg-warning/5 px-4 py-3 hover:border-warning/60"
        >
          <div className="text-sm font-medium text-warning">
            Data quality — {formatConsignments(summary!.unmapped_status.consignments)} without a known status
          </div>
          <div className="text-xs text-muted-foreground mt-0.5">
            {formatLifts(summary!.unmapped_status.lifts)} lifts. Their StatusCode is not Delivered, Out for
            delivery, At depot, or Exception. They stay in today&apos;s denominator — ZeeCore will not guess a
            state it cannot evidence.
          </div>
        </button>
      )}

      <OrdersLaneCards
        deliveries={summary?.deliveries}
        collections={summary?.collections}
        eta={summary?.eta}
        overdueDeliveries={overdueDeliveries}
        overdueCollections={overdueCollections}
        outsideEta={countOutsideEta(exceptions.etas)}
        expiringEta={countExpiringEta(exceptions.etas)}
        etaWarnMinutes={etaWarnMinutes}
        headline={prefs.card_headline}
        etaAvailable={summary?.eta != null && rangeIncludesLondonToday}
        deliveriesProvisional={deliveriesProvisional}
        collectionsProvisional={collectionsProvisional}
        selected={lane}
        loading={loading}
        onSelect={selectLane}
      />

      {prefs.section_order.map((section) => (
        <Fragment key={section}>{renderSection(section)}</Fragment>
      ))}
    </div>
  );
}
